import { request } from './client'
import { asObject, bool, count, optStr, str, text } from './parse'

/**
 * The Telegram account this system listens on.
 *
 * Signing in is not one request. It is a conversation with a phone in somebody's
 * hand: the browser starts it, then polls for what to show — a code to scan, a
 * password to type, a name once it worked. Nothing here holds a request open
 * waiting for a person, because a request held open for a minute is a request
 * some proxy in the middle will close.
 */

/** Where a sign-in has got to. */
export type ChannelPhase =
  /** No account: no session, or the one there was has been destroyed. */
  | 'signed_out'
  /** Between the button and the first code. */
  | 'starting'
  /** A code is on screen waiting for a phone. */
  | 'waiting_for_scan'
  /** The code was approved; the account has a second factor. */
  | 'waiting_for_password'
  /** Signed in, attaching the update stream. */
  | 'connecting'
  /** Working. */
  | 'connected'
  /** Stopped, with a sentence saying why. */
  | 'failed'
  /** This process does not hold the connection at all. */
  | 'unavailable'

export interface ChannelAccount {
  readonly phase: ChannelPhase
  /** False where the deployment is missing Telegram credentials. */
  readonly configured: boolean
  /** Whether the channel is switched on at all (RR_TG_ENABLED). A different
   * fact from the one above, and the two used to be folded together — so a
   * channel that was merely switched off reported "not configured" and the
   * screen listed three possible causes it had checked none of. */
  readonly enabled: boolean
  /** Which credentials are actually absent, when any are. */
  readonly missing: readonly string[]
  /** The login link behind the code, also the cache key for its picture. */
  readonly qrUrl: string | undefined
  readonly qrExpiresAt: string | undefined
  readonly failure: string
  readonly accountId: string | undefined
  readonly telegramId: number
  readonly name: string
  readonly username: string
  /** Enough of the number to recognise the account, not enough to reuse it. */
  readonly phoneMasked: string
}

const PHASES: readonly ChannelPhase[] = [
  'signed_out',
  'starting',
  'waiting_for_scan',
  'waiting_for_password',
  'connecting',
  'connected',
  'failed',
  'unavailable',
]

function phaseOf(value: string): ChannelPhase {
  // An unknown phase means a server newer than this bundle. Reading it as
  // "unavailable" shows the one honest thing — that this screen cannot act —
  // instead of a button whose meaning nobody here knows.
  return (PHASES as readonly string[]).includes(value) ? (value as ChannelPhase) : 'unavailable'
}

function account(body: unknown): ChannelAccount {
  const raw = asObject(body, 'the account')

  return {
    phase: phaseOf(str(raw, 'phase')),
    configured: bool(raw, 'configured'),
    enabled: raw.enabled === true,
    missing: Array.isArray(raw.missing)
      ? raw.missing.filter((each): each is string => typeof each === 'string')
      : [],
    qrUrl: optStr(raw, 'qr_url'),
    qrExpiresAt: optStr(raw, 'qr_expires_at'),
    failure: text(raw, 'failure'),
    accountId: optStr(raw, 'account_id'),
    telegramId: count(raw, 'telegram_id'),
    name: text(raw, 'name'),
    username: text(raw, 'username'),
    phoneMasked: text(raw, 'phone_masked'),
  }
}

export function fetchChannelAccount(signal?: AbortSignal): Promise<ChannelAccount> {
  return request('/api/channel/account', account, signal === undefined ? {} : { signal })
}

export function beginChannelLogin(): Promise<ChannelAccount> {
  return request('/api/channel/login', account, { method: 'POST' })
}

/**
 * Hand over the two-factor password.
 *
 * It goes straight out in the request body and is never put anywhere else: not
 * in a query string, not in the cache, not in a log line. The screen holds it in
 * component state until this call and clears it afterwards.
 */
export function submitChannelPassword(password: string): Promise<ChannelAccount> {
  return request('/api/channel/password', account, { method: 'POST', body: { password } })
}

export function signOutOfChannel(): Promise<ChannelAccount> {
  return request('/api/channel/logout', account, { method: 'POST' })
}

/**
 * The picture of the current login code.
 *
 * The token rides along as a cache key rather than as data: the server draws
 * whatever code is current and ignores it. Without it the browser would keep
 * showing the first code it fetched, and Telegram replaces the code every half
 * a minute.
 */
export function channelQRSource(qrUrl: string): string {
  return `/api/channel/qr.png?token=${encodeURIComponent(qrUrl)}`
}

/** Whether a sign-in is under way, which is what makes the screen poll. */
export function isSigningIn(phase: ChannelPhase): boolean {
  return (
    phase === 'starting' ||
    phase === 'waiting_for_scan' ||
    phase === 'waiting_for_password' ||
    phase === 'connecting'
  )
}
